import { Injectable } from '@angular/core';
import { filterNil } from '@ngneat/elf';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AccessControl, BillingType } from '@pncl/common-models';
import { SubscriptionStatus } from '../models/payment';
import { SpaceRepository } from '../state/space.repository';
import { UserService } from './user.service';
import { TelemetryService } from './telemetry.service';
import Capability = AccessControl.Capability;
import CapabilityDisplayMode = AccessControl.CapabilityDisplayMode;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class CapabilityTierService {
  private capabilityDisplayModeMap = new Map<Capability, BehaviorSubject<CapabilityDisplayMode>>();
  // For illustration; you might inject or configure this differently
  private isLoggingEnabled = true;

  /**
   * IMPORTANT: Space capabilities loaded signal
   *
   * This BehaviorSubject emits 'true' only when a user enters a space and
   * all capabilities for that space have been fully loaded and processed.
   *
   * Purpose:
   * - Provides a clear, reliable signal that space capabilities are ready
   * - Ensures consistent timing for space-specific feature evaluation
   *
   * Usage guidelines:
   * - Use this observable ONLY for space-specific features that should be
   *   evaluated exclusively when inside a space
   * - Always filter for 'true' values to ensure you're only acting when
   *   capabilities are fully loaded
   */
  private spaceCapabilitiesLoadedSubject = new BehaviorSubject<boolean>(false);
  public readonly spaceCapabilitiesLoaded$ = this.spaceCapabilitiesLoadedSubject.asObservable();
  constructor(
    private spaceRepo: SpaceRepository,
    private userService: UserService,
    private telemetryService: TelemetryService,
  ) {
    this.setAllCapabilitiesDisplayMode(CapabilityDisplayMode.HIDE); // Initialize capabilities map
    this.listenToSpaceEnterAndLeaveEvents();
    this.refreshCapabilitiesBasedOnUser();
  }

  private setAllCapabilitiesDisplayMode(displayMode: CapabilityDisplayMode): void {
    // Initialize or reset all capabilities to the provided display mode.
    Object.values(Capability).forEach((capability) => {
      this.setCapabilityDisplayMode(capability, displayMode);
    });
  }

  /**
   * A single method responsible for:
   *  1) Checking if logging is enabled.
   *  2) Determining whether we are INSIDE or OUTSIDE space from isActiveSpaceIdTriggered.
   *  3) Extracting institution Name and capabilities.
   *  4) Logging the assembled message to your telemetry / FullStory service.
   *
   * @param isInsideSpace  If true => "INSIDE SPACE", otherwise => "OUTSIDE SPACE".
   */
  private logCapabilityTierEventToFullStory(isInsideSpace: boolean): void {
    // 1) Short-circuit if not enabled
    if (!this.isLoggingEnabled) {
      return;
    }

    // 2) Determine context
    const context = isInsideSpace ? 'INSIDE SPACE' : 'OUTSIDE SPACE';

    // 3) Choose institution (from space or from user)
    const institution = isInsideSpace
      ? this.spaceRepo.activeSpace?.institution
      : this.userService.user.getValue()?.user.institution;

    // 4) Extract institution name and tier ID (handling undefined cases)
    const institutionName = institution?.name ?? 'N/A';
    const institutionTierId = institution?.tierConfiguration?.tier ?? 'N/A';

    // 5) Choose capabilities (from space or user)
    const capabilities = isInsideSpace
      ? this.spaceRepo.activeSpace?.enabled_capabilities ?? []
      : this.userService.user.getValue()?.user.enabled_capabilities ?? [];

    // 6) Build capabilities string (fall back if empty)
    const capabilitiesStr = capabilities.length ? capabilities.join(', ') : 'N/A';

    // 7) Structure event properties
    const eventProperties = {
      context,
      institutionName,
      institutionTierId,
      enabledCapabilities: capabilitiesStr,
    };

    // 8) Log the event using the `event` function
    this.telemetryService.logBasicEvent('[Enabled Capabilities Logging]', eventProperties);

    const billingStatus = institution?.billingStatus;
    const expiry = billingStatus?.forceBillDate;
    const isExpiredTrial =
      billingStatus?.subscription_status === SubscriptionStatus.INACTIVE &&
      institution?.paywallConfig?.billingType === BillingType.Automatic &&
      !!expiry &&
      Date.now() > new Date(expiry).getTime();

    if (isExpiredTrial) {
      this.telemetryService.logBasicEvent('[Free Trial Expired]', {
        institutionName,
        trialExpiryDate: new Date(expiry!).toISOString(),
      });
    }
  }

  private listenToSpaceEnterAndLeaveEvents(): void {
    merge(this.spaceRepo.activeSpaceId$.pipe(filterNil()), this.spaceRepo.currentUserLeftSpace$)
      .pipe(untilDestroyed(this))
      .subscribe((emittedValue) => {
        const isActiveSpaceIdTriggered = typeof emittedValue === 'string';

        const institutionEnabledCapabilities = isActiveSpaceIdTriggered
          ? this.spaceRepo.activeSpace!.enabled_capabilities
          : this.userService.user.getValue()!.user.enabled_capabilities;

        // Call our new consolidated logging method
        this.logCapabilityTierEventToFullStory(isActiveSpaceIdTriggered);

        this.updateCapabilityDisplayMode(institutionEnabledCapabilities);
        if (isActiveSpaceIdTriggered) {
          this.spaceCapabilitiesLoadedSubject.next(true);
        }
      });
  }

  private refreshCapabilitiesBasedOnUser(): void {
    this.userService.user
      .pipe(filterNil(), distinctUntilChanged(), untilDestroyed(this))
      .subscribe((userData) => {
        /* Dev Comment:
         * We only apply current user's capability
         * when we're not inside an active space. This ensures that the
         * space-specific institution capabilities take precedence when a space is active
         */
        if (!this.spaceRepo.activeSpace) {
          this.logCapabilityTierEventToFullStory(false);
          this.updateCapabilityDisplayMode(userData.user.enabled_capabilities);
        }
      });
  }

  private updateCapabilityDisplayMode(enabledCapabilities: Capability[]): void {
    // If no capabilities are provided, reset all to HIDE.
    if (enabledCapabilities.length === 0) {
      this.setAllCapabilitiesDisplayMode(CapabilityDisplayMode.HIDE);
      return;
    }

    const enabledCapabilitiesSet = new Set(enabledCapabilities);

    Object.values(Capability).forEach((capability) => {
      const newDisplayMode = enabledCapabilitiesSet.has(capability)
        ? CapabilityDisplayMode.SHOW
        : CapabilityDisplayMode.HIDE;

      this.setCapabilityDisplayMode(capability, newDisplayMode);
    });
  }

  private setCapabilityDisplayMode(
    capability: Capability,
    displayMode: CapabilityDisplayMode,
  ): void {
    const subject = this.capabilityDisplayModeMap.get(capability);

    if (subject) {
      // Update purpose
      // If the BehaviorSubject exists, emit the new display mode only if it differs.
      if (subject.value !== displayMode) {
        subject.next(displayMode);
      }
    } else {
      // Initialization purpose
      // If it doesn't exist, create a new BehaviorSubject with the provided display mode.
      this.capabilityDisplayModeMap.set(
        capability,
        new BehaviorSubject<CapabilityDisplayMode>(displayMode),
      );
    }
  }

  // Utility functions related for UI display of a faeature
  // Utility function to determine if a capability's display mode allows it to be shown
  private checkVisibilityModes(displayMode: CapabilityDisplayMode): boolean {
    return displayMode !== CapabilityDisplayMode.HIDE;
  }

  // Helper function to determine if a capability should be rendered (either Render or Disabled state)
  shouldDisplayCapability$(capability: Capability): Observable<boolean> {
    return this.getCapabilityDisplayMode$(capability).pipe(
      map(this.checkVisibilityModes), // Use the common utility function here
    );
  }

  shouldDisplayCapability(capability: Capability): boolean {
    const currentDisplayMode = this.getCapabilityDisplayMode(capability);
    return this.checkVisibilityModes(currentDisplayMode); // Use the common utility function here
  }

  // Utility functions related to Running feature
  private checkEnablementMode(displayMode: CapabilityDisplayMode): boolean {
    return displayMode === CapabilityDisplayMode.SHOW;
  }

  // Helper function to determine if a capability should be Enabled
  shouldEnableCapability$(capability: Capability): Observable<boolean> {
    return this.getCapabilityDisplayMode$(capability).pipe(
      map(this.checkEnablementMode), // Use the common utility function here
    );
  }

  shouldEnableCapability(capability: Capability): boolean {
    const currentDisplayMode = this.getCapabilityDisplayMode(capability);
    return this.checkEnablementMode(currentDisplayMode); // Use the common utility function here
  }

  // Expose Observable for capability's display mode
  private getCapabilityDisplayMode$(capability: Capability): Observable<CapabilityDisplayMode> {
    return this.capabilityDisplayModeMap.get(capability)!.asObservable();
  }

  // Fetches the current display mode of a capability directly
  private getCapabilityDisplayMode(capability: Capability): CapabilityDisplayMode {
    return this.capabilityDisplayModeMap.get(capability)!.value;
  }
}
